import { useShippingMethodSync } from "@/queries/cart";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo } from "react";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface CartShippingAutoSelectProps {
  /** Every seller cart the buyer holds, unfiltered. */
  carts: StoreSellerCart[];
}

/**
 * Gives every supplier cart that has no shipping method its "Standard" one,
 * whatever page or view the buyer is on (KAN-267).
 *
 * The auto-select used to run only inside the Cart view's seller cards, so a
 * cart created while Compare, the header total or the cart drawer was showing
 * had no freight in any figure there until the Cart tab was opened. Mounted
 * once in the app shell it covers all of them, along with Quick Buy and
 * reorder, which land a cart without ever visiting the Cart view.
 *
 * Only carts still lacking a method get a watcher, so a cart that is set up
 * costs nothing beyond the cart list the shell already holds.
 */
export const CartShippingAutoSelect = memo(function CartShippingAutoSelect({
  carts
}: CartShippingAutoSelectProps) {
  return (
    <>
      {carts
        .filter((cart) => !cart.shipping_methods?.length)
        .map((cart) => (
          <CartShippingWatcher key={cart.cart_id} cartId={cart.cart_id} />
        ))}
    </>
  );
});

/** One cart's watcher: a component only because a hook can't be called in a loop. */
function CartShippingWatcher({ cartId }: { cartId: string }) {
  // The same hook the selectors use, so the rule can't drift; the watcher just
  // has no selector to feed. Only method-less carts mount it, hence `null`.
  useShippingMethodSync({ cartId, selectedMethod: null });
  return null;
}
